import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Alert,
    Button,
    RadioGroup,
    Skeleton,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import {
    StepBackButton,
    WorkConfirmDialog,
} from '@/pages/catalogImport/components';
import { workPartsOf, workSentenceOf } from '@/pages/catalogImport/work';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { BranchCard, NewBranchCard } from './components';
import type { UseBranchStepResult } from './useBranchStep';

type BranchStepViewProps = UseBranchStepResult;

// The title is the step's only page-level heading.
const asH1: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h1 {...domProps}>{children}</h1>;

/**
 * Branch: the question, the branches as selectable cards, the inline "Add a
 * branch" card and the page footer: **Back** leftmost (`StepBackButton`
 * labelled with `backStep`: "Accounting" while that step is still needed,
 * "Back" when it opens Welcome) and Continue, which names the selected
 * branch, rightmost; below `sm` Continue first and Back last, both full
 * width (layout classes reorder them, Back stays first in the DOM). A first
 * read that fails shows Retry in place of the cards; a later one that fails
 * keeps the cards and says so above them, with Retry.
 *
 * Coming back after a file was read (`keptFile`), a quiet line under the
 * title says the file and the work on it are kept and that another branch
 * re-checks only the matches. Changing the branch over a file with work
 * asks first (`changeConfirm`): a `WorkConfirmDialog` with a primary
 * Change branch, since the work stays; its body names the work kept and
 * the Products tabs that can change, by their own labels.
 */
export const BranchStepView: React.FC<BranchStepViewProps> = ({
    branches,
    isLoading,
    isLoadError,
    isRefreshError,
    onRetryLoad,
    selectedId,
    selected,
    onSelect,
    isAdding,
    onStartAdd,
    onCancelAdd,
    newName,
    onNewNameChange,
    newAddress,
    onNewAddressChange,
    newError,
    isCreating,
    onCreate,
    onContinue,
    changeConfirm,
    onStayOnBranch,
    onConfirmBranchChange,
    keptFile,
    backStep,
    onBack,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            className="flex flex-col gap-6 py-4 sm:py-8"
            {...getNodeTestIdProp('onboarding-branch')}
        >
            <Typography
                id="onboarding-branch-title"
                render={asH1}
                type="h2"
                {...getNodeTestIdProp('onboarding-branch-title')}
            >
                {t('onboarding.branch.title')}
            </Typography>

            {keptFile === null ? null : (
                <Alert {...getNodeTestIdProp('onboarding-branch-kept')}>
                    <Alert.Indicator>
                        <Icon
                            aria-hidden
                            className="size-4"
                            icon="gravity-ui:file"
                        />
                    </Alert.Indicator>
                    <Alert.Content>
                        <Alert.Description>
                            {t('onboarding.branch.kept', { file: keptFile })}
                        </Alert.Description>
                    </Alert.Content>
                </Alert>
            )}

            {isLoading ? (
                <div
                    className="flex flex-col gap-3"
                    {...getNodeTestIdProp('onboarding-branch-loading')}
                >
                    <Skeleton className="h-20 rounded-xl" />
                    <Skeleton className="h-20 rounded-xl" />
                </div>
            ) : isLoadError ? (
                <Alert
                    status="danger"
                    {...getNodeTestIdProp('onboarding-branch-load-error')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t('onboarding.branch.loadError')}
                        </Alert.Title>
                        <div className="pt-2">
                            <Button
                                onPress={onRetryLoad}
                                size="sm"
                                variant="secondary"
                            >
                                {t('onboarding.branch.retry')}
                            </Button>
                        </div>
                    </Alert.Content>
                </Alert>
            ) : (
                <>
                    {isRefreshError ? (
                        <Alert
                            status="warning"
                            {...getNodeTestIdProp(
                                'onboarding-branch-refresh-error',
                            )}
                        >
                            <Alert.Indicator />
                            <Alert.Content>
                                <Alert.Title>
                                    {t('onboarding.branch.refreshError')}
                                </Alert.Title>
                                <div className="pt-2">
                                    <Button
                                        onPress={onRetryLoad}
                                        size="sm"
                                        variant="secondary"
                                    >
                                        {t('onboarding.branch.retry')}
                                    </Button>
                                </div>
                            </Alert.Content>
                        </Alert>
                    ) : null}
                    <RadioGroup
                        aria-labelledby="onboarding-branch-title"
                        className="flex flex-col gap-3"
                        onChange={onSelect}
                        value={selectedId}
                        {...getNodeTestIdProp('onboarding-branch-cards')}
                    >
                        {branches.map((branch) => (
                            <BranchCard branch={branch} key={branch.id} />
                        ))}
                    </RadioGroup>
                </>
            )}

            <NewBranchCard
                address={newAddress}
                error={newError}
                isCreating={isCreating}
                isOpen={isAdding}
                name={newName}
                onAddressChange={onNewAddressChange}
                onCancel={onCancelAdd}
                onCreate={onCreate}
                onNameChange={onNewNameChange}
                onOpen={onStartAdd}
            />

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <StepBackButton
                    className="order-2 w-full sm:order-1 sm:w-auto"
                    onPress={onBack}
                    target={backStep}
                />
                <Button
                    className="order-1 w-full sm:order-2 sm:w-auto"
                    isDisabled={selected === null}
                    onPress={onContinue}
                    {...getNodeTestIdProp('onboarding-branch-continue')}
                >
                    {selected
                        ? t('onboarding.branch.continueWith', {
                              branch: selected.name,
                          })
                        : t('onboarding.branch.continue')}
                </Button>
            </div>
            <WorkConfirmDialog
                actionLabel={t('onboarding.branch.change.confirm')}
                body={t('onboarding.branch.change.body', {
                    work: changeConfirm
                        ? workSentenceOf(
                              workPartsOf(changeConfirm.work, 'keep').map(
                                  ({ key, params }) => t(key, params),
                              ),
                          )
                        : '',
                    newProducts: t('catalogImport.products.tabs.newProducts'),
                    restock: t('catalogImport.products.tabs.restock'),
                })}
                isOpen={changeConfirm !== null}
                keepLabel={t('onboarding.branch.change.keep')}
                onConfirm={onConfirmBranchChange}
                onKeep={onStayOnBranch}
                title={t('onboarding.branch.change.title', {
                    branch: changeConfirm?.branch ?? '',
                })}
                tone="primary"
            />
        </section>
    );
};
